import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../api_service";
import { getFreshToken } from "../services/deviceAgent";
import { inIframe, requestHrmsToken, decodeRole } from "../services/sso";
import Button from "./ui/Button";
import Icon from "./AppIcon";

// Roles exempt from the device/MAC check (must mirror the backend default).
const BYPASS_ROLES = ["CEO", "CTO"];

// status: 'checking' | 'ok' | 'no-agent' | 'not-approved'
export default function MacGate({ children }) {
  const [status, setStatus] = useState("checking");
  const [mac, setMac] = useState("");

  const runCheck = useCallback(async () => {
    setStatus("checking");
    setMac("");

    // First ask the backend whether MAC enforcement is active. When it is off
    // (the default during rollout), pass straight through so the app behaves
    // normally and no device/agent is required.
    try {
      const pol = await axios.get(`${API_URL}/devices/policy`);
      if (!pol?.data?.enforce) {
        // Best-effort: still try to cache an agent token so requests carry the
        // headers when an agent IS present, but never block on it.
        getFreshToken().catch(() => {});
        setStatus("ok");
        return;
      }
    } catch (err) {
      // If we cannot even reach the policy endpoint, fail open to avoid locking
      // users out of an app whose backend may simply be reachable on another
      // path; the backend filter remains the real enforcement point.
      setStatus("ok");
      return;
    }

    // Role-based exception: CEO/CTO may access TMS from any device, so they
    // skip the device check. Fetch the HRMS token (also caches it for the
    // X-HRMS-AUTH header); the backend independently enforces this.
    if (inIframe()) {
      const hrmsToken = await requestHrmsToken();
      if (hrmsToken) {
        const role = decodeRole(hrmsToken).trim().toUpperCase();
        if (BYPASS_ROLES.includes(role)) {
          setStatus("ok");
          return;
        }
      }
    }

    const token = await getFreshToken();
    if (!token) {
      // Agent not running / unreachable.
      setStatus("no-agent");
      return;
    }

    setMac(token.mac || "");

    try {
      const res = await axios.get(`${API_URL}/devices/agent-check`, {
        params: { mac: token.mac, ts: token.ts, sig: token.sig },
      });
      const data = res?.data || {};
      if (data.verified === true) {
        setStatus("ok");
      } else {
        setStatus("not-approved");
      }
    } catch (err) {
      // Backend unreachable / errored: we cannot confirm the device, so treat
      // it as not approved (a generic message). The backend is the real gate;
      // this is UX only.
      setStatus("not-approved");
    }
  }, []);

  useEffect(() => {
    runCheck();
  }, [runCheck]);

  if (status === "ok") {
    return children;
  }

  if (status === "checking") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground">
        <Icon name="Loader" size={28} className="animate-spin text-primary" />
        <p className="mt-4 text-sm text-muted-foreground">Verifying device…</p>
      </div>
    );
  }

  const isNoAgent = status === "no-agent";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 text-center shadow-sm">
        <div className="flex justify-center mb-4">
          <div className="h-14 w-14 rounded-xl bg-error/10 flex items-center justify-center">
            <Icon name="ShieldAlert" size={30} className="text-error" />
          </div>
        </div>

        {isNoAgent ? (
          <>
            <h1 className="text-xl font-semibold text-foreground">
              Device verification required
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              The WSPulse security agent is not running on this device. Please
              install or start the WSPulse agent, then retry. If the problem
              persists, contact your administrator.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-foreground">
              Device not approved
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              This device has not been approved to access TMS. Please ask your
              administrator to approve the device shown below.
            </p>
            {mac ? (
              <div className="mt-4 flex justify-center">
                <span className="inline-block font-mono text-sm px-3 py-1.5 rounded-full bg-background border border-border text-foreground">
                  {mac}
                </span>
              </div>
            ) : null}
          </>
        )}

        <div className="mt-6">
          <Button onClick={runCheck} iconName="ShieldCheck" fullWidth>
            Retry
          </Button>
        </div>
      </div>
    </div>
  );
}
